import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "./../Footer";
import Header from "./../Header";
import { assetUrl } from "../../utils/api";
import { ClickableImage, useImageLightbox } from "../../components/ImageLightbox";

export default function ConsultantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [consultant, setConsultant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestContact, setGuestContact] = useState("");

  const avatarSrc = consultant
    ? assetUrl(consultant.avatar) || "/default-avatar.png"
    : "";
  const { openAt, lightbox } = useImageLightbox(avatarSrc ? [avatarSrc] : []);

  useEffect(() => {
    fetchConsultant();
  }, [id]);

  const fetchConsultant = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(
        `/api/consultant/profile/${id}`
      );

      if (!res.ok) {
        throw new Error("获取咨询师详情失败");
      }

      const data = await res.json();
      setConsultant(data || null);
    } catch (err) {
      console.error(err);
      setError("暂时无法获取咨询师详情，请稍后再试");
    } finally {
      setLoading(false);
    }
  };

  const handleConsult = () => {
    if (consultant?.is_online === false) {
      alert("该咨询师当前暂停接单");
      return;
    }

    const userId = localStorage.getItem("user_id");

    if (userId) {
      navigate(`/consultant-chat/${consultant.id}`, {
        state: {
          visitorType: "user",
          userId,
        },
      });
      return;
    }

    const savedGuest = getSavedGuest();

    if (savedGuest) {
      navigateToGuestChat(savedGuest);
      return;
    }

    setShowGuestModal(true);
  };

  const handleGuestSubmit = (e) => {
    e.preventDefault();

    const name = guestName.trim();
    const contact = guestContact.trim();

    if (!name) {
      alert("请填写一个称呼，方便咨询师回复你");
      return;
    }

    const guest = {
      id: `guest_${Date.now()}`,
      name,
      contact,
      createdAt: new Date().toISOString(),
    };

    localStorage.setItem("guest_consult_user", JSON.stringify(guest));
    setShowGuestModal(false);
    navigateToGuestChat(guest);
  };

  const navigateToGuestChat = (guest) => {
    navigate(`/consultant-chat/${consultant.id}`, {
      state: {
        visitorType: "guest",
        guest,
      },
    });
  };

  const specialties = splitText(consultant?.specialties);
  const serviceTypes = splitText(consultant?.service_types);
  const canConsult = consultant?.is_online !== false;

  if (loading) {
    return (
      <PageShell>
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-10">
          加载中...
        </main>
      </PageShell>
    );
  }

  if (error || !consultant) {
    return (
      <PageShell>
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-10">
          <div className="bg-white rounded-2xl shadow p-10 text-center">
            <p className="text-gray-500 mb-6">
              {error || "没有找到这位咨询师"}
            </p>
            <button
              onClick={() => navigate("/consultants")}
              className="px-6 py-3 rounded-xl bg-blue-500 text-white font-bold hover:bg-blue-600"
            >
              返回咨询师列表
            </button>
          </div>
        </main>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 text-gray-500 hover:text-blue-500"
        >
          返回上一页
        </button>

        <div className="grid lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <ClickableImage
                  src={avatarSrc}
                  alt={consultant.name}
                  className="w-28 h-28 rounded-full object-cover bg-gray-100"
                  onOpen={() => openAt(0)}
                />

                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h1 className="text-3xl font-bold text-gray-800">
                      {consultant.name || "未命名咨询师"}
                    </h1>

                    <span
                      className={`text-xs px-3 py-1 rounded-full ${
                        consultant.is_verified
                          ? "bg-blue-100 text-blue-600"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {consultant.is_verified ? "已认证" : "未认证"}
                    </span>

                    <span
                      className={
                        canConsult
                          ? "text-xs bg-green-100 text-green-600 px-3 py-1 rounded-full"
                          : "text-xs bg-gray-100 text-gray-500 px-3 py-1 rounded-full"
                      }
                    >
                      {canConsult ? "可咨询" : "暂停接单"}
                    </span>
                  </div>

                  <p className="text-gray-500">
                    {consultant.city || "未填写城市"}
                    {consultant.gender ? ` · ${consultant.gender}` : ""}
                    {consultant.age ? ` · ${consultant.age}岁` : ""}
                    {consultant.experience_years
                      ? ` · ${consultant.experience_years}年经验`
                      : ""}
                  </p>

                  <p className="text-gray-600 mt-4 leading-7">
                    {consultant.intro || "这位咨询师还没有填写简介"}
                  </p>
                </div>
              </div>
            </div>

            <InfoSection title="擅长方向">
              {specialties.length > 0 ? (
                <TagList items={specialties} />
              ) : (
                <p className="text-gray-400">暂未填写</p>
              )}
            </InfoSection>

            <InfoSection title="咨询风格">
              <p className="text-gray-600 leading-7">
                {consultant.consultation_style || "暂未填写咨询风格"}
              </p>
            </InfoSection>

            <InfoSection title="专业背景">
              <p className="text-gray-600 leading-7">
                {consultant.background || "暂未填写专业背景"}
              </p>
            </InfoSection>

            <InfoSection title="服务方式">
              {serviceTypes.length > 0 ? (
                <TagList items={serviceTypes} />
              ) : (
                <p className="text-gray-400">暂未填写</p>
              )}
            </InfoSection>
          </section>

          <aside className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow p-6 lg:sticky lg:top-6">
              <p className="text-gray-400 text-sm mb-2">单次咨询</p>
              <div className="mb-5">
                <span className="text-4xl font-bold text-gray-800">
                  ¥{consultant.price || 0}
                </span>
                <span className="text-gray-400 ml-2">
                  / {consultant.duration || 50}分钟
                </span>
              </div>

              <div className="space-y-3 text-sm text-gray-600 mb-6">
                <InfoLine
                  label="咨询状态"
                  value={canConsult ? "当前可咨询" : "暂停接单"}
                />
                <InfoLine
                  label="登录要求"
                  value="未登录也可先咨询"
                />
                <InfoLine
                  label="付费与订单"
                  value="下单时再登录或绑定手机"
                />
              </div>

              <button
                onClick={handleConsult}
                disabled={!canConsult}
                className={`w-full py-3 rounded-xl text-white font-bold ${
                  canConsult
                    ? "bg-blue-500 hover:bg-blue-600"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                立即咨询
              </button>

              <button
                onClick={() => navigate("/consultants")}
                className="w-full mt-3 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50"
              >
                查看更多咨询师
              </button>
            </div>
          </aside>
        </div>
      </main>

      {showGuestModal ? (
        <GuestConsultModal
          guestName={guestName}
          guestContact={guestContact}
          onNameChange={setGuestName}
          onContactChange={setGuestContact}
          onClose={() => setShowGuestModal(false)}
          onSubmit={handleGuestSubmit}
        />
      ) : null}
      {lightbox}
    </PageShell>
  );
}

function GuestConsultModal({
  guestName,
  guestContact,
  onNameChange,
  onContactChange,
  onClose,
  onSubmit,
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h2 className="text-xl font-bold text-gray-800">先开始咨询</h2>
            <p className="text-sm text-gray-500 mt-1">
              未登录用户也可以先咨询，后续下单或查看历史记录时再登录。
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              怎么称呼你
            </label>
            <input
              value={guestName}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="例如：王女士"
              className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              联系方式
              <span className="text-gray-400 font-normal ml-1">选填</span>
            </label>
            <input
              value={guestContact}
              onChange={(e) => onContactChange(e.target.value)}
              placeholder="手机号或微信号，方便后续联系"
              className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          <button
            type="submit"
            className="w-full py-3 rounded-xl bg-blue-500 text-white font-bold hover:bg-blue-600"
          >
            进入咨询
          </button>
        </form>
      </div>
    </div>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      {children}
      <Footer />
    </div>
  );
}

function InfoSection({ title, children }) {
  return (
    <div className="bg-white rounded-2xl shadow p-6">
      <h2 className="text-xl font-bold text-gray-800 mb-4">{title}</h2>
      {children}
    </div>
  );
}

function TagList({ items }) {
  return (
    <div className="flex flex-wrap gap-3">
      {items.map((item) => (
        <span
          key={item}
          className="bg-blue-50 text-blue-600 px-4 py-2 rounded-full text-sm"
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function InfoLine({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-gray-400">{label}</span>
      <span className="text-gray-700 text-right">{value || "暂未填写"}</span>
    </div>
  );
}

function splitText(text) {
  if (!text) {
    return [];
  }

  return String(text)
    .split(/[,，、\s]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function getSavedGuest() {
  try {
    const raw = localStorage.getItem("guest_consult_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    localStorage.removeItem("guest_consult_user");
    return null;
  }
}
